import type { OHLCV } from '@shared/types/candle'

import { registerIndicator } from '@/indicators/registry'
import type { IndicatorConfig, IndicatorResult } from '@/types/indicator'
import { IndicatorBase } from '@/types/indicator'

const CONFIG: IndicatorConfig = {
  id: 'pivots',
  name: 'Pivot Points (Standard)',
  shortName: 'Pivots',
  category: 'professional',
  overlay: true,
  params: [],
  lines: [
    { key: 'r3', label: 'R3', color: '#b71c1c', width: 1, style: 'dashed' },
    { key: 'r2', label: 'R2', color: '#e53935', width: 1, style: 'dashed' },
    { key: 'r1', label: 'R1', color: '#ef5350', width: 1, style: 'dashed' },
    { key: 'pp', label: 'PP', color: '#b2bec3', width: 2 },
    { key: 's1', label: 'S1', color: '#26a69a', width: 1, style: 'dashed' },
    { key: 's2', label: 'S2', color: '#1b876a', width: 1, style: 'dashed' },
    { key: 's3', label: 'S3', color: '#00695c', width: 1, style: 'dashed' },
  ],
}

function buildDailyMap(candles: OHLCV[]): Map<number, { h: number; l: number; c: number }> {
  const m = new Map<number, { h: number; l: number; c: number }>()
  for (const c of candles) {
    const day = Math.floor(c.t / 86400)
    const existing = m.get(day)
    if (!existing) {
      m.set(day, { h: c.h, l: c.l, c: c.c })
    } else {
      if (c.h > existing.h) existing.h = c.h
      if (c.l < existing.l) existing.l = c.l
      existing.c = c.c
    }
  }
  return m
}

class PivotPoints extends IndicatorBase {
  readonly config = CONFIG

  calculate(candles: OHLCV[]): IndicatorResult {
    const n = candles.length
    const r3: (number | null)[] = new Array<number | null>(n).fill(null)
    const r2: (number | null)[] = new Array<number | null>(n).fill(null)
    const r1: (number | null)[] = new Array<number | null>(n).fill(null)
    const pp: (number | null)[] = new Array<number | null>(n).fill(null)
    const s1: (number | null)[] = new Array<number | null>(n).fill(null)
    const s2: (number | null)[] = new Array<number | null>(n).fill(null)
    const s3: (number | null)[] = new Array<number | null>(n).fill(null)

    if (n < 2)
      return {
        lines: [
          { key: 'r3', values: r3 },
          { key: 'r2', values: r2 },
          { key: 'r1', values: r1 },
          { key: 'pp', values: pp },
          { key: 's1', values: s1 },
          { key: 's2', values: s2 },
          { key: 's3', values: s3 },
        ],
      }

    const dailyMap = buildDailyMap(candles)
    const sortedDays = [...dailyMap.keys()].sort((a, b) => a - b)
    const prevDay = new Map<number, { h: number; l: number; c: number }>()
    for (let i = 1; i < sortedDays.length; i++) {
      prevDay.set(sortedDays[i], dailyMap.get(sortedDays[i - 1])!)
    }

    for (let i = 0; i < n; i++) {
      const day = Math.floor(candles[i].t / 86400)
      const prev = prevDay.get(day)
      if (!prev) continue

      const pivot = (prev.h + prev.l + prev.c) / 3
      const range = prev.h - prev.l

      pp[i] = pivot
      r1[i] = 2 * pivot - prev.l
      r2[i] = pivot + range
      r3[i] = prev.h + 2 * (pivot - prev.l)
      s1[i] = 2 * pivot - prev.h
      s2[i] = pivot - range
      s3[i] = prev.l - 2 * (prev.h - pivot)
    }

    return {
      lines: [
        { key: 'r3', values: r3 },
        { key: 'r2', values: r2 },
        { key: 'r1', values: r1 },
        { key: 'pp', values: pp },
        { key: 's1', values: s1 },
        { key: 's2', values: s2 },
        { key: 's3', values: s3 },
      ],
    }
  }
}

registerIndicator(PivotPoints, CONFIG)
